import { useRef } from 'react'
import { toast } from 'sonner'
import { PhotoProvider } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import type { FlightLogSectionContext } from '../useFlightLogForm'
import { isUploadedDocument, type FlightLogDocumentField } from '../form'
import { UploadFileRow } from '../../ui/upload-file-row'
import { PendingThumbnail, PhotoThumbnail } from '../../TaskPhotos'
import { isNative } from '@/lib/native-file-viewer'

const MAX_FILE_SIZE = 150 * 1024 * 1024 // 150MB

/**
 * Attached paperwork photos (fuel slips, load sheets, …) as a square
 * thumbnail grid with a corner X. Picks stage locally as object-URL
 * previews — the upload happens in useFlightLogForm when the pilot saves a
 * draft or submits, and removals likewise only persist on the next save.
 */
export function DocumentsSection({ context }: { context: FlightLogSectionContext }) {
  const { values, setValue, readOnly } = context
  const documents = values.documents ?? []
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    // Reset input so the same files can be selected again
    e.target.value = ''
    if (files.length === 0) return

    const staged: FlightLogDocumentField[] = []
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`)
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds the 150 MB limit`)
        continue
      }
      staged.push({ file, fileName: file.name, previewUrl: URL.createObjectURL(file) })
    }
    if (staged.length > 0) {
      setValue('documents', [...documents, ...staged], { shouldDirty: true })
    }
  }

  const handleRemove = (index: number) => {
    const doc = documents[index]
    if (doc && !isUploadedDocument(doc)) URL.revokeObjectURL(doc.previewUrl)
    setValue(
      'documents',
      documents.filter((_, i) => i !== index),
      { shouldDirty: true }
    )
  }

  const body = (
    <div className="flex flex-col gap-2">
      {documents.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {documents.map((doc, index) =>
            isUploadedDocument(doc) ? (
              <PhotoThumbnail
                key={doc.s3Key}
                s3Key={doc.s3Key}
                onRemove={readOnly ? undefined : () => handleRemove(index)}
              />
            ) : (
              <PendingThumbnail
                key={doc.previewUrl}
                previewUrl={doc.previewUrl}
                onRemove={() => handleRemove(index)}
              />
            )
          )}
        </div>
      )}

      {readOnly ? (
        documents.length === 0 && (
          <p className="py-1 text-sm text-content-primary-subtle">No documents attached</p>
        )
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          <UploadFileRow onSelect={handleFileSelect} />
        </>
      )}
    </div>
  )

  // PhotoProvider powers the web lightbox inside PhotoThumbnail/
  // PendingThumbnail; native taps open the system viewer instead.
  return isNative ? body : <PhotoProvider>{body}</PhotoProvider>
}
